/**
 * Automated Booking Validation Engine
 *
 * Thin client-side wrapper around the canonical rule set in
 * supabase/functions/_shared/bookingRules.ts — that file is the single
 * source of truth for both this client check and the server-side
 * validate-booking Edge Function, so the two can no longer drift apart.
 */

import type { StrataConfig } from "@/types/database";
import { evaluateBookingRules } from "../../supabase/functions/_shared/bookingRules";

export interface BookingValidationInput {
  nights: number;
  guestCount: number;
  checkIn: string; // ISO date
  checkOut: string;
  unitNumber: string;
  hostStatus: string;
  hostExpiresAt?: string | null;
  hostNightsUsed: number;
  hostNightLimit: number;
}

export interface ValidationCheck {
  rule: string;
  passed: boolean;
  message: string;
}

export interface BookingValidationResult {
  isValid: boolean;
  status: "approved" | "blocked";
  checks: ValidationCheck[];
  blockedReason?: string;
}

export function validateBooking(
  input: BookingValidationInput,
  config: StrataConfig
): BookingValidationResult {
  const result = evaluateBookingRules({ ...input, strataConfig: config });

  return {
    isValid: result.isValid,
    status: result.isValid ? "approved" : "blocked",
    checks: result.checks,
    blockedReason: result.blockedReason,
  };
}

/**
 * Calculate the fee for a booking based on strata config
 */
export function calculateBookingFee(
  nights: number,
  baseAmount: number,
  config: StrataConfig
): { strataFee: number; totalFee: number } {
  let strataFee = 0;

  switch (config.fee_type) {
    case "flat":
      strataFee = config.fee_amount;
      break;
    case "percentage":
      strataFee = (baseAmount * config.fee_amount) / 100;
      break;
    case "per_night":
      strataFee = nights * config.fee_amount;
      break;
  }

  return {
    strataFee: Math.round(strataFee * 100) / 100,
    totalFee: Math.round((baseAmount + strataFee) * 100) / 100,
  };
}
